import React, { useState, useEffect } from 'react';
import { useLoader } from '../../../context/LoaderContext';
import { useSearchParams } from 'react-router-dom';
import { SearchBar, TitleBar, DropDown, PaginatedTable } from '../../common';
import { RECEIPT_COLUMN, ROUTES, TITLES, BUTTON, GROUP_BY, LOCAL_STORAGE } from '../../../constants';
import { useDebounce } from '../../../hooks';
import { vendorReceipt, getGroupBy, validateDispatch, packageCreationDyeing } from '../../../service';
import { getLocalStorageItem, receiptCompiler, setLocalStorageItem, popup } from '../../../utils';
import { Button, InputWithSearch } from '../../shared';

// Constant for the localStorage key to store available group values
const AVAILABLE_GROUP_VALUES_KEY = 'receipt_available_group_values';

export const ReceiptContent = () => {
  const { setLoading } = useLoader();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params or default values
  const [receiptData, setReceiptsData] = useState([]);
  const [paginationData, setPaginationData] = useState({});
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [search, setSearch] = useState(false);
  const [sort1, setSort1] = useState(searchParams.get('filter') || RECEIPT_COLUMN[10].name);
  const [sortingArray1, setSortingArray1] = useState([]);
  const [groupArray, setGroupArray] = useState([]);
  const [groupBy, setGroupBy] = useState(searchParams.get('groupBy') || GROUP_BY.TITLE);
  const [availableValuesGroupBy, setAvailableValuesGroupBy] = useState([]);
  const [selectedItem, setSelectedItem] = useState(
    searchParams.get('groupItems') ? searchParams.get('groupItems').split(',') : [],
  );
  const [pageNumber, setPageNumber] = useState(parseInt(searchParams.get('page')) || 1);
  const [groupApplied, setGroupApplied] = useState(searchParams.get('groupApplied') === 'true');
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [isRestoringSession, setIsRestoringSession] = useState(true);
  const [receiptMode, setReceiptMode] = useState(searchParams.get('receiptMode') === 'true' ? 1 : 0);
  const [receiptList, setReceiptList] = useState([]);
  const [filteredColumn, setFilteredColumn] = useState(RECEIPT_COLUMN);
  const [isLoading, setIsLoading] = useState(false);
  const [isGroupReceitsLoading, setIsGroupReceitsLoading] = useState(false);
  const [selectionMode, setSelectionMode] = useState("page"); // "page" | "all"
  const [excludedIds, setExcludedIds] = useState(new Set());
  const [globalSelectAll, setGlobalSelectAll] = useState(false);
  const [selectableRecords, setSelectableRecords] = useState(null);

  // Load cached available values from localStorage on component mount
  useEffect(() => {
    try {
      // Try to restore available values from localStorage
      const cachedValues = getLocalStorageItem(AVAILABLE_GROUP_VALUES_KEY) || {};
      const currentGroupBy = searchParams.get('groupBy') || GROUP_BY.TITLE;

      if (cachedValues[currentGroupBy] && Array.isArray(cachedValues[currentGroupBy])) {
        setAvailableValuesGroupBy(cachedValues[currentGroupBy]);
      }
    } catch (error) {
      console.error('Error loading cached group values:', error);
    }
  }, []);


  useEffect(() => {
    if (!receiptMode) {
      setReceiptList([]);
    }

    const vendorType = getLocalStorageItem('vendor_type');
    const isSpecialVendor = vendorType === 'clothing' || vendorType === 'dyeing';

    let columns = RECEIPT_COLUMN.filter((col) => {
      if (!receiptMode && col.name === '*') return false;

      if (!isSpecialVendor && col.name === 'Finished Product') return false;
      if (col.name === 'Container No' && vendorType === 'clothing') {
        return false; // Hide Container No if vendor is fabric or dyeing
      }
      return true;
    });

    setFilteredColumn(columns);
  }, [receiptMode]);

  // Cache available values to localStorage whenever they change
  useEffect(() => {
    // Skip the initial setting when component first mounts
    if (!isRestoringSession && availableValuesGroupBy.length > 0) {
      try {
        const cachedValues = getLocalStorageItem(AVAILABLE_GROUP_VALUES_KEY) || {};

        // Update the cached values for the current groupBy
        const updatedCache = {
          ...cachedValues,
          [groupBy]: availableValuesGroupBy,
        };

        setLocalStorageItem(AVAILABLE_GROUP_VALUES_KEY, updatedCache);
      } catch (error) {
        console.error('Error caching group values:', error);
      }
    }

    // After first render, mark restoration as complete
    if (isRestoringSession) {
      setIsRestoringSession(false);
    }
  }, [availableValuesGroupBy, groupBy, isRestoringSession]);

  const updateUrlParams = (params = {}) => {
    const newParams = {};

    // Only include non-empty values
    if (params.search) newParams.search = params.search;
    if (params.filter) newParams.filter = params.filter;
    if (params.page) newParams.page = params.page.toString();
    if (params.groupBy && params.groupBy !== GROUP_BY.TITLE) newParams.groupBy = params.groupBy;

    // Fix for groupItems - extract only IDs before joining them in URL
    if (params.groupItems && params.groupItems.length > 0) {
      const ids = params.groupItems.map((item) => {
        // If item is an array [id, name], extract only the ID
        if (Array.isArray(item) && item.length > 1) {
          return item[0];
        }
        // If it's already a string ID or simple value, use as is
        return item;
      });
      newParams.groupItems = ids.join(',');
    }

    if (params.groupApplied === true) newParams.groupApplied = 'true';

    setSearchParams(newParams, { replace: true });
  };


  const fetchReceipts = useDebounce((params = {}) => {
    const updatedParams = { ...params };
    if (
      updatedParams.groupby_field === 'vendor_id,partner_id' &&
      updatedParams.groupby_specifics &&
      Object.keys(updatedParams.groupby_specifics).length > 0
    ) {
      const specifics = updatedParams.groupby_specifics;

      updatedParams.groupby_specifics = {
        vendor_id: specifics.vendor_id || [],
        selection: {
          partner_id: specifics.partner_id || [],
          vendor_id: false,
        },
      };
    }

    vendorReceipt({ data: updatedParams })
      .then((response) => {
        if (response?.data?.records?.length > 0) {
          setReceiptsData(response.data.records);
          setPaginationData(response.data.params);
          setGroupArray(response?.data?.groupby || []);
          setSelectableRecords(response?.data?.selectable_records || 0)

          const name = RECEIPT_COLUMN[10].dataField[0];
          if (response?.data[name] && Array.isArray(response?.data[name])) {
            setSortingArray1(response?.data[name]);
          }
        } else {
          setReceiptsData([]);
        }
      })
      .catch((error) => {
        console.error('Error fetching receipts:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, 400);

  const fetchGroupBY = useDebounce((params = {}) => {
    getGroupBy({ data: params })
      .then((response) => {
        if (response?.data && response?.data[groupBy]) {
          setAvailableValuesGroupBy(response?.data[groupBy]);
        }
      })
      .catch((error) => {
        console.error('Error fetching group by options:', error);
        // If the API fails, try to use cached values
        try {
          const cachedValues = getLocalStorageItem(AVAILABLE_GROUP_VALUES_KEY) || {};
          if (cachedValues[groupBy] && Array.isArray(cachedValues[groupBy])) {
            setAvailableValuesGroupBy(cachedValues[groupBy]);
          }
        } catch (err) {
          console.error('Error using backup cached values:', err);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, 400);

  // Helper function to refresh groupBy options with clean parameters when needed
  const refreshGroupByOptions = (includeFilters = true) => {
    if (initialLoadComplete && groupArray.includes(groupBy)) {
      let params = {
        related_to: GROUP_BY.RECEIPT,
        field_name: groupBy,
      };

      // Only include search and sort if requested
      if (includeFilters) {
        if (searchTerm) {
          params.search = {
            name: searchTerm,
            origin: searchTerm,
            'move_ids.future_lot': searchTerm,
            invoice_no: searchTerm,
            'vendor_id.name': searchTerm,
            'partner_id.name': searchTerm,
            partner_ref: searchTerm,
            finished_products: searchTerm,
            physical_container_no: searchTerm,
          };
        }
        if (sort1 !== RECEIPT_COLUMN[10].name) {
          params.sort = { selection: { state: sort1.toLowerCase() } };
        }
      }
      fetchGroupBY(params);
    }
  };

  const shouldApplyGroupBy = () => {
    // Only apply groupBy if:
    // 1. groupApplied flag is true
    // 2. groupBy is a valid option in groupArray
    // 3. At least one item is selected OR we're just applying the groupBy field
    return (
      groupApplied &&
      ((groupArray.length > 0 && groupArray.includes(groupBy)) || initialLoadComplete === false) &&
      (selectedItem.length > 0 || !searchParams.get('groupItems'))
    );
  };

  const getReceiptsList = (additionalParams = {}) => {
    setIsLoading(true);
    setGlobalSelectAll(false);
    setSelectionMode("page");
    // setReceiptList([]);

    const shouldUseGroupBy = shouldApplyGroupBy();
    // Define base parameters
    let params = {
      page_number: additionalParams.page_number || pageNumber,
      search: searchTerm
        ? {
          name: searchTerm,
          origin: searchTerm,
          'move_ids.future_lot': searchTerm,
          invoice_no: searchTerm,
          'vendor_id.name': searchTerm,
          'partner_id.name': searchTerm,
          partner_ref: searchTerm,
          finished_products: searchTerm,
          physical_container_no: searchTerm,
        }
        : {},
      sort:
        sort1 && sort1 !== RECEIPT_COLUMN[10].name
          ? { selection: { state: sort1.toLowerCase() } }
          : {},
      groupby_field: shouldUseGroupBy ? groupBy : '',
      ...additionalParams,
    };

    if (shouldUseGroupBy && selectedItem.length) {
      if (groupBy.includes(',')) {
        // For comma-separated fields, create an object with each field as a key
        const fields = groupBy.split(',');
        const groupbySpecifics = {};

        fields.forEach((field) => {
          groupbySpecifics[field.trim()] = selectedItem.map((item) => {
            // If item is an array [id, name], extract only the ID
            if (Array.isArray(item) && item.length > 1) {
              // Convert ID to number if possible
              const id = item[0];
              return !isNaN(Number(id)) ? Number(id) : id;
            }
            // If it's already a value, try to convert to number if possible
            return !isNaN(Number(item)) ? Number(item) : item;
          });
        });

        params.groupby_specifics = groupbySpecifics;
      } else {
        // For single field, use the original format but ensure we extract IDs as numbers
        params.groupby_specifics = {
          [groupBy]: selectedItem.map((item) => {
            // If item is an array [id, name], extract only the ID
            if (Array.isArray(item) && item.length > 1) {
              // Convert ID to number if possible
              const id = item[0];
              return !isNaN(Number(id)) ? Number(id) : id;
            }
            // If it's already a value, try to convert to number if possible
            return !isNaN(Number(item)) ? Number(item) : item;
          }),
        };
      }
    } else {
      params.groupby_specifics = {};
    }
    // Update URL params
    updateUrlParams({
      search: searchTerm,
      filter: sort1 !== RECEIPT_COLUMN[10].name ? sort1 : '',
      page: params.page_number,
      groupBy: shouldUseGroupBy ? groupBy : '',
      groupItems: shouldUseGroupBy && selectedItem.length ? selectedItem : [],
      groupApplied: shouldUseGroupBy,
    });

    fetchReceipts(params);
  };

  const removeGroupBy = () => {
    setGroupApplied(false);
    setSelectedItem([]);
    setAvailableValuesGroupBy([]);
    setGroupBy(GROUP_BY.TITLE);

    // Update URL - remove group params but keep existing sort filter
    updateUrlParams({
      search: searchTerm,
      filter: sort1 !== RECEIPT_COLUMN[10].name ? sort1 : '',
      page: 1,
      // Explicitly omit groupBy, groupItems, and groupApplied
    });

    // Fetch data with groupBy removed
    getReceiptsList({
      page_number: 1,
      groupby_field: '',
      groupby_specifics: {},
    });
  };

  // Handle GroupBy selection change
  const handleGroupByChange = (newGroupBy) => {
    if (newGroupBy === groupBy) return;

    setGroupBy(newGroupBy);
    // When changing the groupBy field, reset selected items
    setSelectedItem([]);

    // Apply the groupBy automatically when it's a valid option
    if (groupArray.includes(newGroupBy)) {
      setGroupApplied(true);

      // Try to load cached values first for a smoother experience
      try {
        const cachedValues = getLocalStorageItem(AVAILABLE_GROUP_VALUES_KEY) || {};
        if (cachedValues[newGroupBy] && Array.isArray(cachedValues[newGroupBy])) {
          setAvailableValuesGroupBy(cachedValues[newGroupBy]);
        }
      } catch (err) {
        console.error('Error using cached values for new groupBy:', err);
      }
    }
  };

  // Initial load - read from URL params
  useEffect(() => {
    // On initial load, if we have params in the URL, use them
    const urlSearchTerm = searchParams.get('search');
    const urlFilter = searchParams.get('filter');
    const urlPage = searchParams.get('page');
    const urlGroupBy = searchParams.get('groupBy');
    const urlGroupItems = searchParams.get('groupItems');
    const urlGroupApplied = searchParams.get('groupApplied');

    let hasGroupByParams = false;

    if (urlSearchTerm && urlSearchTerm !== searchTerm) {
      setSearchTerm(urlSearchTerm);
    }

    if (urlFilter && urlFilter !== sort1) {
      setSort1(urlFilter);
    }

    if (urlPage && parseInt(urlPage) !== pageNumber) {
      setPageNumber(parseInt(urlPage));
    }

    if (urlGroupBy && urlGroupBy !== groupBy) {
      setGroupBy(urlGroupBy);
      hasGroupByParams = true;
    }

    if (urlGroupItems) {
      const items = urlGroupItems.split(',');

      // Find the corresponding full items from availableValuesGroupBy if possible
      const fullItems = items.map((id) => {
        // Try to find a matching item in availableValuesGroupBy
        const match = availableValuesGroupBy.find(
          (item) => Array.isArray(item) && item[0].toString() === id.toString(),
        );

        // If found, return the full [id, name] array, otherwise return just the id
        return match || id;
      });

      if (JSON.stringify(fullItems) !== JSON.stringify(selectedItem)) {
        setSelectedItem(fullItems);
        hasGroupByParams = true;
      }
    }

    if (urlGroupApplied === 'true' && !groupApplied) {
      setGroupApplied(true);
      hasGroupByParams = true;
    }

    // Check if we have cached values for the current groupBy
    const cachedValues = getLocalStorageItem(AVAILABLE_GROUP_VALUES_KEY) || {};
    const hasCachedValues =
      urlGroupBy && cachedValues[urlGroupBy] && Array.isArray(cachedValues[urlGroupBy]);

    // If we have cached values, use them for a faster initial render
    if (hasCachedValues) {
      setAvailableValuesGroupBy(cachedValues[urlGroupBy]);
    }

    // Check if we need to fetch group options
    if (hasGroupByParams && urlGroupBy) {
      // Pre-fetch the group options if needed
      const params = {
        related_to: GROUP_BY.RECEIPT,
        field_name: urlGroupBy,
        search: urlSearchTerm
          ? {
            name: urlSearchTerm,
            origin: urlSearchTerm,
            lot_name: urlSearchTerm,
            invoice_no: urlSearchTerm,
            'vendor_id.name': urlSearchTerm,
            'partner_id.name': urlSearchTerm,
            partner_ref: urlSearchTerm,
            physical_container_no: urlSearchTerm,
          }
          : {},
        sort:
          urlFilter !== RECEIPT_COLUMN[10].name
            ? { selection: { state: urlFilter.toLowerCase() } }
            : {},
      };

      // Fetch group by options first
      fetchGroupBY(params);
    }

    // Always fetch data on initial load to ensure all filters are applied
    getReceiptsList();

    // Mark initial load as complete
    setInitialLoadComplete(true);
  }, []);

  // Handle selected items change for groupBy
  useEffect(() => {
    // Skip initial render
    if (initialLoadComplete) {
      // Apply groupBy automatically when items are selected
      setGroupApplied(true);

      // Reset to page 1 only when first applying the groupBy
      // (when there were no items before but now there are)
      const prevItems = searchParams.get('groupItems');
      const wasEmpty = !prevItems || prevItems.split(',').length === 0;

      if (wasEmpty) {
        setPageNumber(1);
        getReceiptsList({ page_number: 1 });
      } else {
        // Keep the current page number for subsequent selections
        getReceiptsList({ page_number: pageNumber });
      }
    }
  }, [selectedItem]);

  // Handle search
  useEffect(() => {
    if (search) {
      setPageNumber(1); // Reset page number on new search
      getReceiptsList({ page_number: 1 });

      // Also refresh the group by options if needed
      if (initialLoadComplete && groupArray.includes(groupBy)) {
        refreshGroupByOptions(true); // Include filters since we're adding a search
      }
    }
  }, [search]);

  // Handle page changes
  useEffect(() => {
    // Only fetch when pageNumber changes and it's not the initial load
    const currentUrlPage = searchParams.get('page');
    if (initialLoadComplete && currentUrlPage !== pageNumber.toString() && pageNumber !== 0) {
      getReceiptsList();
    }
  }, [pageNumber]);

  // Handle sort changes
  useEffect(() => {
    // Skip initial render
    if (initialLoadComplete) {
      // Check if sort1 has changed from its initial URL value
      const initialSort = searchParams.get('filter') || RECEIPT_COLUMN[10].name;
      if (sort1 !== initialSort) {
        setPageNumber(1); // Reset page number on sorting change
        getReceiptsList({ page_number: 1 });

        // Also refresh the group by options if needed
        if (groupArray.includes(groupBy)) {
          // Check if we're removing filter (going back to default)
          const includeFilters = sort1 !== RECEIPT_COLUMN[10].name;
          refreshGroupByOptions(includeFilters);
        }
      }
    }
  }, [sort1]);

  // Fetch group by values when groupBy changes
  useEffect(() => {
    if (initialLoadComplete && groupArray.includes(groupBy)) {
      // When changing the groupBy field, we want to show all options
      // regardless of current filters
      refreshGroupByOptions(false); // Don't include filters for initial groupBy options
    }
  }, [groupBy]);

  // Handle empty search term
  useEffect(() => {
    if (initialLoadComplete && !searchTerm && search) {
      setPageNumber(1);
      setSearch(false);
      getReceiptsList({ page_number: 1 });

      // If search is cleared and groupBy is applied, refresh the groupBy options
      // without any search filter
      if (groupArray.includes(groupBy)) {
        refreshGroupByOptions(false); // Don't include filters when search is removed
      }
    }
  }, [searchTerm]);

  // Apply groupBy when groupApplied changes
  useEffect(() => {
    if (initialLoadComplete) {
      // Keep the current page number when groupApplied changes
      getReceiptsList({ page_number: 1 });
    }
  }, [groupApplied]);

  const resetFilter = () => {
    setSort1(RECEIPT_COLUMN[10].name);
    setPageNumber(1);
    getReceiptsList({ page_number: 1 });

    // Refresh groupBy options without filters when filters are cleared
    if (groupArray.includes(groupBy)) {
      refreshGroupByOptions(false);
    }
  };

  const handleAddReceipts = () => {
    receiptMode ? (setReceiptMode(0), setReceiptList([]), setSelectionMode("page"), setGlobalSelectAll(false)) : setReceiptMode(1);
  };

  const addBulkReceipts = ({ id, contact }) => {
    if (selectionMode === "all") {
      const next = new Set(excludedIds);
      if (excludedIds.has(id)) {
        next.delete(id); // re-check
      } else {
        next.add(id);    // uncheck
      }
      setExcludedIds(next);
    } else {
      if (receiptList.some((re) => re.id === id)) {
        setReceiptList((prev) => prev.filter((re) => re.id !== id));
      } else {
        setReceiptList((prev) => [{ id, contact }, ...prev]);
      }
    }
  };

  const checked = (id, isDisabled) => {
    if (selectionMode === "all") {
      return !(excludedIds.has(id) || isDisabled);
    }
    return receiptList.some((re) => re.id === id);
  };

  const handleSelectAllVisible = () => {
    const visibleAssignedIds = receiptData
      .filter(row => (row.state !== 'done' && row.state !== 'cancel') && (row.fill_received_quantity ? row.received_quantity_filled : true))
      .map(row => row.id);
    visibleAssignedIds.forEach(id => {
      if (!checked(id)) {
        addBulkReceipts({ id });
      }
    });
    setSelectionMode("page");
    setExcludedIds(new Set());
  };

  const handleSelectAllMatching = () => {
    setSelectionMode("all");
    setExcludedIds(new Set());
    setGlobalSelectAll(true);
    setReceiptList([]);
  };

  const handleSubmit = async (receipts, additionalParams = {}) => {
    setIsGroupReceitsLoading(true);

    try {
      let promises = [];
      const vendorType = getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE);

      if (selectionMode === "all") {
        const shouldUseGroupBy = shouldApplyGroupBy();
        let params = {
          page_number: additionalParams.page_number || pageNumber,
          search: searchTerm
            ? {
              name: searchTerm,
              origin: searchTerm,
              "move_ids.future_lot": searchTerm,
              invoice_no: searchTerm,
              "vendor_id.name": searchTerm,
              "partner_id.name": searchTerm,
              partner_ref: searchTerm,
              finished_products: searchTerm,
              physical_container_no: searchTerm,
            }
            : {},
          sort:
            sort1 && sort1 !== RECEIPT_COLUMN[10].name
              ? { selection: { state: sort1.toLowerCase() } }
              : {},
          groupby_field: shouldUseGroupBy ? groupBy : "",
          ...additionalParams,
        };

        if (shouldUseGroupBy && selectedItem.length) {
          if (groupBy.includes(",")) {
            const fields = groupBy.split(",");
            const groupbySpecifics = {};

            fields.forEach((field) => {
              groupbySpecifics[field.trim()] = selectedItem.map((item) => {
                if (Array.isArray(item) && item.length > 1) {
                  const id = item[0];
                  return !isNaN(Number(id)) ? Number(id) : id;
                }
                return !isNaN(Number(item)) ? Number(item) : item;
              });
            });

            params.groupby_specifics = groupbySpecifics;
          } else {
            params.groupby_specifics = {
              [groupBy]: selectedItem.map((item) => {
                if (Array.isArray(item) && item.length > 1) {
                  const id = item[0];
                  return !isNaN(Number(id)) ? Number(id) : id;
                }
                return !isNaN(Number(item)) ? Number(item) : item;
              }),
            };
          }
        } else {
          params.groupby_specifics = {};
        }

        // ---- Wrap params in your API payload ----
        const validateDispatchPayload = {
          select_all: true,
          picking_ids: [],
          ...params,
          excluded_ids: Array.from(excludedIds),
          group_dispatch: true,
        };

        const packageCreationDyeingPayload = {
          select_all: true,
          picking_id: [],
          ...params,
          excluded_ids: Array.from(excludedIds),
          group_dispatch: true,
        };

        // Always call validateDispatch
        promises.push(validateDispatch({ data: validateDispatchPayload }));

        // Call second API only if vendorType === clothing
        if (vendorType === "clothing") {
          promises.push(packageCreationDyeing({ data: packageCreationDyeingPayload }));
        }
      } else {
        const standardReceipts = receipts.filter(
          (item) => item.contact === "STANDARD APPAREL"
        );
        const nonStandardReceipts = receipts.filter(
          (item) => item.contact !== "STANDARD APPAREL"
        );

        const nonStandardIds = nonStandardReceipts.map((item) => item.id);
        const standardIds = standardReceipts.map((item) => item.id);

        if (nonStandardIds.length > 0) {
          promises.push(validateDispatch({ data: { picking_ids: nonStandardIds } }));
        }

        if (vendorType === "clothing" && standardIds.length > 0) {
          promises.push(packageCreationDyeing({ data: { picking_id: standardIds } }));
        }
      }

      // ---- Run requests in parallel ----
      const responses = await Promise.all(promises);

      setReceiptList([]);
      setReceiptMode(receiptMode ? 0 : 1);
      getReceiptsList();

      responses.forEach((res) => {
        if (res) popup("success", res.message);
      });
    } catch (error) {
      popup("error", error?.message || "Something went wrong");
    } finally {
      setIsGroupReceitsLoading(false);
    }
  };

  return (
    <>
      <TitleBar title={[{ text: TITLES.RECEIPT, route: ROUTES.RECEIPT }]} />
      <div className="d-flex align-items-center justify-content-start gap-3 w-100">
        <SearchBar
          search={search}
          setSearchTerm={setSearchTerm}
          searchTerm={searchTerm}
          setSearch={setSearch}
          onDefaultSearch={() => getReceiptsList({ page_number: 1 })}
        />
        <div className="w-50 mb-3 filter-btns-div d-flex align-items-center justify-content-start">
          <DropDown dropdownMenu={sortingArray1} value={sort1} setValue={setSort1} />
          {sort1 !== RECEIPT_COLUMN[10].name && (
            <Button
              clickHandler={resetFilter}
              className="blue-btn d-flex align-items-center justify-content-center px-3">
              {BUTTON.CLEAR_FILTERS}
            </Button>
          )}
        </div>
      </div>
      <div className="d-block align-items-center justify-content-start gap-3 w-100">
        <div className="w-50 mb-3 filter-btns-div d-flex align-items-center justify-content-start">
          <DropDown dropdownMenu={groupArray} value={groupBy} setValue={handleGroupByChange} />
          {groupArray.includes(groupBy) && (
            <InputWithSearch
              setAvailableItem={setAvailableValuesGroupBy}
              availableItem={availableValuesGroupBy}
              selectedItem={selectedItem}
              setSelectedItem={setSelectedItem}
            />
          )}
          {groupArray.includes(groupBy) && (
            <Button
              clickHandler={removeGroupBy}
              className="blue-btn d-flex align-items-center justify-content-center px-3">
              {BUTTON.REMOVE}
            </Button>
          )}
        </div>

        {/* Updated Records Selected + Action Buttons section - same as Bills */}
        {Array.isArray(receiptData) && receiptData?.length > 0 &&
          (getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) === 'clothing' || getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) === 'dyeing') && (
            <div className="d-flex justify-content-between align-items-center mb-3 w-100">
              {/* Left: Buttons + Selected count */}
              <div className="d-flex align-items-center gap-3">
                <Button
                  clickHandler={handleAddReceipts}
                  className="blue-btn d-flex align-items-center justify-content-center px-3">
                  {receiptMode ? BUTTON.CANCEL : BUTTON.GROUP_RECEIPTS}
                </Button>

                {(receiptMode && !globalSelectAll) ? (
                  <>
                    <Button
                      clickHandler={handleSelectAllVisible}
                      className="blue-btn d-flex align-items-center justify-content-center px-3">
                      Select Page
                    </Button>
                  </>
                ) : ''}

                {(receiptList.length > 0 && !globalSelectAll) && (
                  <>
                    <span className="text-muted ms-2">
                      {receiptList.length} {receiptList.length === 1 ? 'record' : 'records'} selected
                    </span>
                    {!(receiptList.length === selectableRecords) && <span
                      onClick={handleSelectAllMatching}
                      className="text-primary cursor-pointer fw-semibold"
                      role="button">
                      Select All ({selectableRecords})
                    </span>}
                  </>
                )}
                {selectionMode === 'all' && (
                  <>
                    <span className="text-muted ms-2">
                      {selectableRecords - excludedIds.size}{' '}
                      {(selectableRecords - excludedIds.size) === 1 ? 'record' : 'records'} selected
                    </span>
                    {!(receiptList.length === selectableRecords) && excludedIds.size > 0 && <span
                      onClick={handleSelectAllMatching}
                      className="text-primary cursor-pointer fw-semibold"
                      role="button">
                      Select All ({selectableRecords})
                    </span>}
                  </>
                )}
              </div>

              {/* Right: Submit button */}
              <div className="d-flex">
                {(receiptList.length > 0 || selectionMode === 'all') && (
                  <Button
                    clickHandler={() => handleSubmit(receiptList)}
                    disabled={isGroupReceitsLoading}
                    className={`blue-btn d-flex align-items-center justify-content-center px-3 ${!isGroupReceitsLoading ? '' : 'disabled'}`}>
                    {isGroupReceitsLoading && (
                      <span
                        className="spinner-border spinner-border-sm text-white me-2"
                        role="status"
                      />
                    )}
                    {BUTTON.SUBMIT}
                  </Button>
                )}
              </div>
            </div>
          )
        }
      </div>


      {Array.isArray(receiptData) ? (
        <PaginatedTable
          columns={filteredColumn}
          initialData={receiptData}
          colFilter={false}
          to={receiptMode ? '' : ROUTES.RECEIPT}
          compiler={{ use: true, tool: receiptCompiler }}
          func={{ addBulkReceipts, checked }}
          pageNumber={pageNumber}
          className="table-4"
          setPageNumber={setPageNumber}
          paginationData={paginationData}
          isLoading={isLoading}
        />
      ) : (
        <div>
          <h4 className="text-center px-1 py-5">No Records Found</h4>
        </div>
      )}
    </>
  );
};
